import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Radio,
  Switch,
  Tag,
  Space,
  Tooltip,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Typography,
  Tabs,
  Divider,
  Alert,
  Spin,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  LinkOutlined,
  UploadOutlined,
  BookOutlined,
  SearchOutlined,
  MedicineBoxOutlined,
  ExperimentOutlined,
  HeartOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import MedlinePlusContentCard from './MedlinePlusContentCard';
import MedlinePlusConnect from './MedlinePlusConnect';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://care360-backend.onrender.com';

const PatientEducation = ({ patientId }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [educationMaterials, setEducationMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [form] = Form.useForm();
  const [documentType, setDocumentType] = useState('file');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchText, setSearchText] = useState('');

  // MedlinePlus Connect state
  const [activeTab, setActiveTab] = useState('library');
  const [medlinePlusResults, setMedlinePlusResults] = useState([]);
  const [medlinePlusLoading, setMedlinePlusLoading] = useState(false);
  const [codeSearchForm] = Form.useForm();
  const [selectedCodeSystem, setSelectedCodeSystem] = useState('ICD-10-CM');

  // New user-friendly health search state
  const [healthSearchForm] = Form.useForm();
  const [selectedHealthCategory, setSelectedHealthCategory] = useState('conditions');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

  // Education categories
  const categories = [
    { key: 'General Health', label: t('healthLibrary.generalHealth') },
    { key: 'Medications', label: t('healthLibrary.medications') },
    { key: 'Procedures', label: t('healthLibrary.procedures') },
    { key: 'Lifestyle', label: t('categories.lifestyle') },
    { key: 'Nutrition', label: t('healthLibrary.nutrition') },
    { key: 'Exercise', label: t('healthLibrary.exercise') },
    { key: 'Mental Health', label: t('healthLibrary.mentalHealth') },
    { key: 'Chronic Conditions', label: t('healthLibrary.chronicConditions') },
    { key: 'Preventive Care', label: t('healthLibrary.preventiveCare') },
    { key: 'Post-Procedure Care', label: t('healthLibrary.postProcedureCare') },
    { key: 'Emergency Information', label: t('healthLibrary.emergencyInformation') },
    { key: 'Other', label: t('common.other') }
  ];

  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  // Health information search categories - user-friendly approach
  const healthSearchCategories = [
    {
      key: 'conditions',
      label: 'Medical Conditions & Diseases',
      icon: <HeartOutlined />,
      description: 'Find information about diagnoses, symptoms, and conditions',
      placeholder: 'e.g., diabetes, hypertension, pneumonia',
      examples: ['Type 2 Diabetes', 'High Blood Pressure', 'Heart Disease', 'Asthma']
    },
    {
      key: 'medications',
      label: 'Medications & Drugs',
      icon: <MedicineBoxOutlined />,
      description: 'Get details about medications, side effects, and usage',
      placeholder: 'e.g., metformin, lisinopril, aspirin',
      examples: ['Metformin', 'Lisinopril', 'Aspirin', 'Insulin']
    },
    {
      key: 'procedures',
      label: 'Medical Procedures & Tests',
      icon: <ExperimentOutlined />,
      description: 'Learn about medical procedures, surgeries, and diagnostic tests',
      placeholder: 'e.g., colonoscopy, MRI, blood test',
      examples: ['Colonoscopy', 'MRI Scan', 'Blood Glucose Test', 'X-Ray']
    },
    {
      key: 'symptoms',
      label: 'Symptoms & Signs',
      icon: <BookOutlined />,
      description: 'Understand symptoms and what they might indicate',
      placeholder: 'e.g., chest pain, shortness of breath, fever',
      examples: ['Chest Pain', 'Shortness of Breath', 'Fever', 'Headache']
    }
  ];

  // MedlinePlus Connect code systems (kept for backend compatibility)
  const codeSystems = [
    { value: 'ICD-10-CM', label: 'ICD-10-CM (Diagnosis)', icon: <HeartOutlined /> },
    { value: 'ICD-9-CM', label: 'ICD-9-CM (Diagnosis)', icon: <HeartOutlined /> },
    { value: 'SNOMED-CT', label: 'SNOMED CT (Diagnosis)', icon: <HeartOutlined /> },
    { value: 'RXCUI', label: 'RXCUI (Medication)', icon: <MedicineBoxOutlined /> },
    { value: 'NDC', label: 'NDC (Medication)', icon: <MedicineBoxOutlined /> },
    { value: 'LOINC', label: 'LOINC (Lab Test)', icon: <ExperimentOutlined /> },
    { value: 'CPT', label: 'CPT (Procedure)', icon: <BookOutlined /> }
  ];

  const fetchEducationMaterials = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      let url = `/patients/${patientId}/education`;
      if (selectedCategory !== 'all') {
        url += `?category=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setEducationMaterials(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch education materials: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [patientId, selectedCategory]);

  useEffect(() => {
    fetchEducationMaterials();
  }, [fetchEducationMaterials]);

  // User-friendly health information search
  const searchHealthInformation = async (values) => {
    setMedlinePlusLoading(true);
    try {
      const { category, searchTerm } = values;
      const translateToVietnamese = currentLanguage === 'vi';

      // Create a search request that will be handled by a new backend endpoint
      // For now, we'll use a simple mapping approach
      const response = await axios.post('/medlineplus/search-by-term', {
        category,
        searchTerm: searchTerm.trim(),
        translate: translateToVietnamese,
        useCache: true
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setMedlinePlusResults(response.data.data);
        message.success(`Found ${response.data.data.length} health information result(s)!`);
      } else {
        setMedlinePlusResults([]);
        message.info('No health information found for this search term. Try different keywords or check spelling.');
      }
    } catch (error) {
      console.error('Error searching health information:', error);
      if (error.response?.status === 404) {
        message.info('No health information found for this search term. Try different keywords.');
      } else {
        message.error('Failed to search health information. Please try again.');
      }
      setMedlinePlusResults([]);
    } finally {
      setMedlinePlusLoading(false);
    }
  };

  // MedlinePlus Connect search function (for advanced users)
  const searchMedlinePlusByCode = async (values) => {
    setMedlinePlusLoading(true);
    try {
      const { codeSystem, code, codeName } = values;
      const translateToVietnamese = currentLanguage === 'vi';

      const response = await axios.get(`/medlineplus/code/${codeSystem}/${code}`, {
        params: {
          codeName: codeName || '',
          translate: translateToVietnamese,
          useCache: true
        }
      });

      if (response.data.success && response.data.data) {
        const result = response.data.data;
        setMedlinePlusResults([result]);
        message.success('Health information found successfully!');
      } else {
        setMedlinePlusResults([]);
        message.info('No health information found for this code.');
      }
    } catch (error) {
      console.error('Error searching MedlinePlus:', error);
      setMedlinePlusResults([]);

      if (error.response?.status === 429) {
        message.error('Rate limit exceeded. Please try again later.');
      } else if (error.response?.status === 404) {
        message.info('No health information found for this code.');
      } else {
        message.error('Failed to search health information: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setMedlinePlusLoading(false);
    }
  };

  // Function to save MedlinePlus content as education material
  const saveMedlinePlusAsEducation = async (medlinePlusData) => {
    try {
      setLoading(true);

      const isVietnamese = currentLanguage === 'vi';
      const content = isVietnamese && medlinePlusData.vietnameseContent
        ? medlinePlusData.vietnameseContent
        : medlinePlusData.originalContent;

      const educationData = {
        title: content.title,
        description: content.summary || '',
        category: 'General Health',
        documentType: 'link',
        externalLink: content.url,
        priority: 'Medium',
        isVisibleInPortal: true,
        tags: `MedlinePlus,${medlinePlusData.codeSystem},${medlinePlusData.code}`
      };

      const response = await axios.post(`/patients/${patientId}/education`, educationData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data.success) {
        message.success('MedlinePlus content saved to Health Library!');
        fetchEducationMaterials(); // Refresh the list
      }
    } catch (error) {
      message.error('Failed to save MedlinePlus content: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch patient's medical codes and suggest health information
  const fetchPatientMedicalCodes = async () => {
    try {
      setMedlinePlusLoading(true);

      // Fetch patient's problems (ICD-10 codes)
      const problemsResponse = await axios.get(`/patients/${patientId}/problems`);
      const problems = problemsResponse.data.data || [];

      // Fetch patient's medications
      const medicationsResponse = await axios.get(`/patients/${patientId}/medications`);
      const medications = medicationsResponse.data.data || [];

      // Extract codes for MedlinePlus search
      const codes = [];

      // Add ICD-10 codes from problems
      problems.forEach(problem => {
        if (problem.icd10Code) {
          codes.push({
            codeSystem: 'ICD-10-CM',
            code: problem.icd10Code,
            codeName: problem.problemName || problem.problemName_vn
          });
        }
      });

      // Add medication codes (if available)
      medications.forEach(medication => {
        if (medication.rxcui) {
          codes.push({
            codeSystem: 'RXCUI',
            code: medication.rxcui,
            codeName: medication.medicationName
          });
        } else if (medication.ndc) {
          codes.push({
            codeSystem: 'NDC',
            code: medication.ndc,
            codeName: medication.medicationName
          });
        }
      });

      if (codes.length === 0) {
        message.info('No medical codes found for this patient. You can search manually using the form above.');
        setMedlinePlusResults([]);
        return;
      }

      // Limit to first 5 codes to avoid overwhelming the API
      const limitedCodes = codes.slice(0, 5);

      // Search MedlinePlus for each code
      const translateToVietnamese = currentLanguage === 'vi';
      const results = [];

      for (const codeInfo of limitedCodes) {
        try {
          const response = await axios.get(`/medlineplus/code/${codeInfo.codeSystem}/${codeInfo.code}`, {
            params: {
              codeName: codeInfo.codeName || '',
              translate: translateToVietnamese,
              useCache: true
            }
          });

          if (response.data.success && response.data.data) {
            results.push(response.data.data);
          }

          // Small delay between requests
          await new Promise(resolve => setTimeout(resolve, 500));

        } catch (error) {
          console.error(`Error fetching for code ${codeInfo.code}:`, error);
          // Continue with other codes
        }
      }

      setMedlinePlusResults(results);

      if (results.length > 0) {
        message.success(`Found health information for ${results.length} of ${limitedCodes.length} medical codes.`);
      } else {
        message.info('No health information found for the patient\'s medical codes.');
      }

    } catch (error) {
      console.error('Error fetching patient medical codes:', error);
      message.error('Failed to fetch patient medical codes: ' + (error.response?.data?.message || error.message));
    } finally {
      setMedlinePlusLoading(false);
    }
  };

  const handleAddMaterial = () => {
    setEditingMaterial(null);
    setDocumentType('file');
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setDocumentType(material.documentType);
    form.setFieldsValue({
      title: material.title,
      description: material.description,
      category: material.category,
      priority: material.priority,
      externalLink: material.externalLink,
      isVisibleInPortal: material.isVisibleInPortal,
      tags: material.tags?.join(', ')
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('description', values.description || '');
      formData.append('category', values.category);
      formData.append('documentType', documentType);
      formData.append('priority', values.priority || 'Medium');
      formData.append('isVisibleInPortal', values.isVisibleInPortal !== false);
      
      if (values.tags) {
        formData.append('tags', values.tags);
      }

      if (documentType === 'link') {
        formData.append('externalLink', values.externalLink);
      } else if (values.educationFile && values.educationFile.length > 0) {
        formData.append('educationFile', values.educationFile[0].originFileObj);
      }

      let response;
      if (editingMaterial) {
        // Update existing material
        response = await axios.put(`/education/${editingMaterial._id}`, values, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // Create new material
        response = await axios.post(`/patients/${patientId}/education`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.data.success) {
        message.success(editingMaterial ? 'Health library material updated successfully' : 'Health library material added successfully');
        setIsModalVisible(false);
        fetchEducationMaterials();
      }
    } catch (error) {
      message.error('Failed to save health library material: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingMaterial(null);
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      setLoading(true);
      const response = await axios.delete(`/education/${materialId}`);
      if (response.data.success) {
        message.success('Education material deleted successfully');
        fetchEducationMaterials();
      }
    } catch (error) {
      message.error('Failed to delete education material: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const onDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    form.setFieldsValue({ educationFile: undefined, externalLink: undefined });
  };

  // Filter materials based on search text
  const filteredMaterials = educationMaterials.filter(material =>
    material.title.toLowerCase().includes(searchText.toLowerCase()) ||
    material.description?.toLowerCase().includes(searchText.toLowerCase()) ||
    material.category.toLowerCase().includes(searchText.toLowerCase()) ||
    material.tags?.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => {
        let url;
        if (record.documentType === 'link') {
          url = record.externalLink;
          return <a href={url} target="_blank" rel="noopener noreferrer">{text} <LinkOutlined /></a>;
        }
        // Open file in new tab/page (same as patient documents)
        const token = localStorage.getItem('authToken');
        const fileUrl = `${BACKEND_URL}/education/file/${record._id}${token ? `?token=${token}` : ''}`;
        return <a href={fileUrl} target="_blank" rel="noopener noreferrer">{text}</a>;
      }
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag color="blue">{category}</Tag>
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const colors = { Low: 'green', Medium: 'orange', High: 'red', Critical: 'purple' };
        return <Tag color={colors[priority] || 'default'}>{priority}</Tag>;
      }
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (type) => type.charAt(0).toUpperCase() + type.slice(1)
    },
    {
      title: 'Portal Visible',
      dataIndex: 'isVisibleInPortal',
      key: 'isVisibleInPortal',
      render: (visible) => visible ? <Tag color="success">Yes</Tag> : <Tag>No</Tag>
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (date) => moment(date).format('MM/DD/YYYY HH:mm')
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      render: (user) => user?.name || user?.firstName + ' ' + user?.lastName || 'Unknown'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        let downloadViewUrl = '';
        if (record.documentType === 'file') {
          // Open file in new tab/page (same as patient documents)
          const token = localStorage.getItem('authToken');
          downloadViewUrl = `${BACKEND_URL}/education/file/${record._id}${token ? `?token=${token}` : ''}`;
        } else if (record.documentType === 'link') {
          downloadViewUrl = record.externalLink;
        }

        return (
          <Space size="middle">
            <Tooltip title="Edit Material">
              <Button icon={<EditOutlined />} onClick={() => handleEditMaterial(record)} />
            </Tooltip>
            
            <Tooltip title="Delete Material">
              <Popconfirm
                title="Are you sure you want to delete this education material?"
                onConfirm={() => handleDeleteMaterial(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Button icon={<DeleteOutlined />} danger />
              </Popconfirm>
            </Tooltip>

            {downloadViewUrl && (
              <Tooltip title={record.documentType === 'file' ? "View File" : "Open Link"}>
                <Button
                  icon={record.documentType === 'file' ? <DownloadOutlined /> : <LinkOutlined />}
                  href={downloadViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        style={{ marginBottom: 16 }}
      >
        <TabPane
          tab={
            <span>
              <BookOutlined />
              Health Library
            </span>
          }
          key="library"
        >
          <Card>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder={t('search.searchHealthLibrary')}
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder={t('search.filterByCategory')}
                  style={{ width: '100%' }}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                >
                  <Option value="all">{t('search.allCategories')}</Option>
                  {categories.map(category => (
                    <Option key={category.key} value={category.key}>{category.label}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddMaterial}
                  style={{ width: '100%' }}
                >
                  Add Health Library Material
                </Button>
              </Col>
            </Row>
          </Card>

          <Table
            columns={columns}
            dataSource={filteredMaterials}
            loading={loading}
            rowKey="_id"
            scroll={{ x: true }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <GlobalOutlined />
              Health Information Search
              {currentLanguage === 'vi' && <Tag color="blue" style={{ marginLeft: 8 }}>Vietnamese</Tag>}
            </span>
          }
          key="medlineplus"
        >
          <MedlinePlusConnect />
        </TabPane>
      </Tabs>

      {/* Add/Edit Modal */}
      <Modal
        title={editingMaterial ? t('healthLibrary.edit') : t('healthLibrary.addNew')}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        destroyOnClose
        width={700}
      >
        <Form form={form} layout="vertical" name="educationForm">
          <Form.Item
            name="title"
            label={t('healthLibrary.materialTitle')}
            rules={[{ required: true, message: t('healthLibrary.pleaseEnterTitle') }]}
          >
            <Input placeholder={t('healthLibrary.enterTitle')} />
          </Form.Item>

          <Form.Item name="description" label={t('healthLibrary.description')}>
            <TextArea rows={3} placeholder={t('healthLibrary.enterDescription')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="category" 
                label="Category" 
                rules={[{ required: true, message: 'Please select a category!' }]}
              >
                <Select placeholder="Select category">
                  {categories.map(category => (
                    <Option key={category.key} value={category.key}>{category.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" initialValue="Medium">
                <Select>
                  {priorities.map(priority => (
                    <Option key={priority} value={priority}>{priority}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="tags" label="Tags (comma-separated)">
            <Input placeholder="e.g., diabetes, diet, exercise" />
          </Form.Item>

          <Form.Item name="isVisibleInPortal" label="Visible in Patient Portal" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>

          <Form.Item name="documentType" label="Material Type" initialValue={documentType}>
            <Radio.Group onChange={onDocumentTypeChange} disabled={editingMaterial}>
              <Radio value="file">Upload File</Radio>
              <Radio value="link">External Link</Radio>
            </Radio.Group>
          </Form.Item>

          {!editingMaterial && (
            <>

              {documentType === 'file' ? (
                <Form.Item 
                  name="educationFile" 
                  label="Upload File"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                  rules={[{ required: true, message: 'Please upload a file!' }]}
                >
                  <Upload 
                    beforeUpload={() => false}
                    maxCount={1}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mov"
                  >
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                </Form.Item>
              ) : (
                <Form.Item 
                  name="externalLink" 
                  label="External Link"
                  rules={[
                    { required: true, message: 'Please enter a link!' },
                    { type: 'url', message: 'Please enter a valid URL!' }
                  ]}
                >
                  <Input placeholder="https://example.com" />
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default PatientEducation;
